import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { clearToken, getToken, setToken } from "@/lib/tokenStore";

const AuthContext = createContext(null);

// Loads /auth/me if a token is present. Returns an unmount-guarded async runner.
function bootstrapSession(setUser, setLoading) {
  let mounted = true;
  api
    .get("/auth/me")
    .then((r) => {
      if (mounted) setUser(r.data);
    })
    .catch((err) => {
      if (!mounted) return;
      // Any /auth/me failure means the stored token is stale or the API is
      // unreachable — wipe locally so the UI treats the user as logged out.
      console.warn("auth bootstrap failed:", err?.response?.status || err?.message);
      clearToken();
      setUser(null);
    })
    .finally(() => {
      if (mounted) setLoading(false);
    });
  return () => {
    mounted = false;
  };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Reading a stored token on first mount is intentionally NOT reactive —
    // it's a bootstrap-only signal, so it doesn't belong in the dep array.
    if (!getToken()) {
      setLoading(false);
      return undefined;
    }
    return bootstrapSession(setUser, setLoading);
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setToken(data.token);
    const me = await api.get("/auth/me");
    setUser(me.data);
    return me.data;
  }, []);

  const register = useCallback(async (email, password, name) => {
    const { data } = await api.post("/auth/register", { email, password, name });
    setToken(data.token);
    const me = await api.get("/auth/me");
    setUser(me.data);
    return me.data;
  }, []);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch (err) {
      console.warn("auth refresh failed:", err?.response?.status || err?.message);
      return null;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Server-side session invalidation is best-effort — local cleanup below
      // always runs so the UI reliably logs the user out.
      console.warn("auth logout API failed:", err?.response?.status || err?.message);
    }
    clearToken();
    setUser(null);
  }, []);

  // login/register/refresh/logout are all stable (empty-dep useCallback),
  // so the context value only needs to change when user or loading change.
  const value = useMemo(
    () => ({ user, loading, login, register, logout, refresh }),
    [user, loading, login, register, logout, refresh]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
export { formatApiError };
